import { create } from 'zustand';
import config from '../config';
import { setAccessToken, clearAccessToken } from '../apiHelpers';
import useNavPrefsStore from './useNavPrefsStore';

/**
 * THE capability list. One array, because this shape used to be hand-copied in
 * six places and that is exactly how can_manager, can_procurement, can_swarm and
 * can_financial went missing from the LOGIN path while the refresh path carried
 * them: a capability added to the API reached whichever copies somebody
 * remembered. A bookkeeper who signed in could not open Invoices until she
 * reloaded the page.
 *
 * Adding a capability is now one edit here.
 */
export const CAPABILITY_FLAGS = [
  'can_admin',
  'can_operations',
  'can_manufacturing',
  'can_producer',
  'can_sales',
  'can_ai',
  'can_analyst',
  'can_pilot',
  'can_manager',
  'can_procurement',
  'can_swarm',
  'can_financial',
  'can_rnd',
  'can_connect_bot', 'can_animator',
];

const defaultUser = {
  firstname: '',
  lastname: '',
  id: '',
  email: '',
  usertype: '',
  isadmin: false,
  loggedin: false,
  ...Object.fromEntries(CAPABILITY_FLAGS.map((flag) => [flag, false])),
  primary_role: 'staff',
  travel_tier: null,
};

/**
 * The API's user payload in the store's shape. Every caller that has just been
 * handed a user by the server uses this: the login form, the invite redemption,
 * and the refresh on app mount. Flags are coerced, because the API sends real
 * booleans but a missing one must read false rather than undefined.
 */
export const userFromApi = (apiUser) => ({
  firstname: apiUser.firstname,
  lastname: apiUser.lastname || '',
  id: apiUser.id,
  email: apiUser.email,
  usertype: apiUser.usertype,
  isadmin: !!apiUser.isadmin,
  loggedin: true,
  ...Object.fromEntries(CAPABILITY_FLAGS.map((flag) => [flag, !!apiUser[flag]])),
  primary_role: apiUser.primary_role || 'staff',
  travel_tier: apiUser.travel_tier || null,
});

const useUserStore = create((set, get) => ({
  // State
  user: { ...defaultUser },
  isInitializing: true,

  // Actions
  setUser: (userData) => {
    set({ user: userData });
    // Persist to localStorage for session recovery
    if (userData.loggedin) {
      localStorage.setItem('firstname', userData.firstname);
      localStorage.setItem('lastname', userData.lastname || '');
      localStorage.setItem('id', userData.id);
      localStorage.setItem('email', userData.email);
      localStorage.setItem('usertype', userData.usertype);
      localStorage.setItem('isadmin', userData.isadmin);
      localStorage.setItem('loggedin', 'true');
      // Capability flags
      CAPABILITY_FLAGS.forEach((flag) => localStorage.setItem(flag, userData[flag]));
      localStorage.setItem('primary_role', userData.primary_role || 'staff');
      localStorage.setItem('travel_tier', userData.travel_tier || '');
    }
  },

  logout: async () => {
    // Clear access token from memory
    clearAccessToken();

    // Clear only firefleet-specific localStorage keys (not all localStorage)
    ['firstname', 'lastname', 'id', 'email', 'usertype', 'isadmin', 'loggedin', 'name',
     ...CAPABILITY_FLAGS, 'primary_role', 'travel_tier'
    ].forEach(key => {
      localStorage.removeItem(key);
    });

    // Reset user state
    set({ user: { ...defaultUser } });

    // Clear per-device nav prefs so bookmarks/recents don't leak to the next
    // user on a shared device (RBAC still gates access, but recents can reveal
    // the prior user's navigation).
    useNavPrefsStore.setState({ bookmarks: [], recents: [], flerpieHidden: false });

    // Try to revoke refresh token on backend (best effort)
    try {
      await fetch(`${config.API_ENDPOINT}/login/logout/jwt`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Error during logout:', error);
    }
  },

  // Initialize auth on app mount
  initializeAuth: async (onSuccess) => {
    try {
      const response = await fetch(`${config.API_ENDPOINT}/login/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const data = await response.json();
        setAccessToken(data.accessToken);

        set({ user: userFromApi(data.user) });

        // Callback to trigger data fetch
        if (onSuccess) onSuccess();
      } else {
        // Refresh failed - load from localStorage but mark as not logged in
        get().loadFromLocalStorage();
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      get().loadFromLocalStorage();
    } finally {
      set({ isInitializing: false });
    }
  },

  loadFromLocalStorage: () => {
    const hasStoredData = localStorage.getItem('loggedin') === 'true';
    set({
      user: {
        firstname: localStorage.getItem('firstname') || '',
        lastname: localStorage.getItem('lastname') || '',
        id: localStorage.getItem('id') || '',
        email: localStorage.getItem('email') || '',
        usertype: localStorage.getItem('usertype') || '',
        isadmin: localStorage.getItem('isadmin') === 'true',
        loggedin: hasStoredData,
        // Capability flags
        ...Object.fromEntries(
          CAPABILITY_FLAGS.map((flag) => [flag, localStorage.getItem(flag) === 'true']),
        ),
        primary_role: localStorage.getItem('primary_role') || 'staff',
        travel_tier: localStorage.getItem('travel_tier') || null,
      },
    });
  },

  // Helper: check if user has any of the given capabilities
  hasCapability: (...capabilities) => {
    const user = get().user;
    if (user.can_admin) return true;
    return capabilities.some(cap => user[`can_${cap}`]);
  },
  // NOTE: there is intentionally no admin-bypassed helper for `financial`.
  // Cost/price visibility is opt-in even for admins, so surfaces read
  // `user.can_financial` directly (and nav uses userCanAccess('financial')).
}));

export default useUserStore;
